import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Network,
    CheckCircle,
    RefreshCw,
    ShieldCheck,
    Zap,
    Save,
    Activity,
    Info,
    Shuffle,
    HelpCircle,
    Trash2,
    AlertTriangle,
    Maximize2,
    X,
    ArrowRightLeft,
    Settings,
    Layers,
    Terminal,
    Compass
} from 'lucide-react';
import { API_BASE_URL } from '../../config';
import './PrivateConnectivity.css';
import './PrivateConnectivityRedesign.css';
import ContextCredentialModal from './ContextCredentialModal';

const ProviderLogo = ({ provider, size = 18, className = "" }) => {
    const logos = {
        aws: 'https://www.vectorlogo.zone/logos/amazon_aws/amazon_aws-icon.svg',
        gcp: 'https://www.vectorlogo.zone/logos/google_cloud/google_cloud-icon.svg',
        azure: 'https://www.vectorlogo.zone/logos/microsoft_azure/microsoft_azure-icon.svg'
    };
    return (
        <img
            src={logos[provider]}
            alt={provider.toUpperCase()}
            className={`${className} ${provider === 'aws' ? 'aws-logo-theme-fix' : ''}`}
            style={{ width: provider === 'aws' ? Math.round(size * 1.6) : size, height: size, objectFit: 'contain', verticalAlign: 'middle' }}
        />
    );
};

const getHelpContent = (cloud, field) => {
    const helpData = {
        aws: {
            creds: {
                title: "AWS Credentials",
                steps: [
                    "Sign in to the AWS Management Console.",
                    "Go to IAM Dashboard -> Users.",
                    "Select your username, click the Security credentials tab.",
                    "Scroll to Access keys and click Create access key to generate your Access Key ID and Secret Access Key."
                ]
            },
            region: {
                title: "AWS Region",
                steps: [
                    "AWS Regions are geographic zones (e.g., ap-south-1 for Mumbai, us-east-1 for N. Virginia).",
                    "You can find your active region in the top-right corner of the AWS Console."
                ]
            },
            vpc: {
                title: "AWS VPC ID",
                steps: [
                    "Navigate to the VPC Dashboard in the AWS Console.",
                    "Click on Your VPCs in the left navigation menu.",
                    "Copy the VPC ID of your target VPC (e.g., vpc-04c133065ad5be269)."
                ]
            },
            asn: {
                title: "AWS BGP ASN",
                steps: [
                    "Border Gateway Protocol Autonomous System Number.",
                    "Use any private ASN in the 64512-65534 range.",
                    "AWS Virtual Private Gateway default is 64512."
                ]
            }
        },
        gcp: {
            creds: {
                title: "GCP Credentials",
                steps: [
                    "Go to GCP Console -> IAM & Admin -> Service Accounts.",
                    "Select or create your service account.",
                    "Under the Keys tab, click Add Key -> Create new key.",
                    "Choose JSON format to download the key file, then upload it in the Settings module."
                ]
            },
            region: {
                title: "GCP Region",
                steps: [
                    "GCP Regions represent locations where resources run (e.g., asia-south1 for Mumbai, us-central1 for Iowa).",
                    "Verify your region under Compute Engine -> Regions and zones or VPC networks."
                ]
            },
            vpc: {
                title: "GCP VPC Network Name",
                steps: [
                    "Navigate to VPC network -> VPC networks in the GCP Console.",
                    "Copy the exact Name of your target network (e.g., default or your custom VPC name)."
                ]
            },
            asn: {
                title: "GCP BGP ASN",
                steps: [
                    "Private ASN configured on the GCP Cloud Router for dynamic BGP routing.",
                    "Must be in the range 64512-65534 or 4200000000-4294967294 (except 16550, which is reserved by GCP)."
                ]
            }
        },
        azure: {
            creds: {
                title: "Azure Credentials",
                steps: [
                    "Open the Azure Portal and go to Microsoft Entra ID.",
                    "Navigate to App registrations -> New registration to create a Service Principal.",
                    "Copy the Application (client) ID and Tenant ID.",
                    "Under Certificates & secrets, click New client secret to generate a Secret Key."
                ]
            },
            region: {
                title: "Azure Region",
                steps: [
                    "Azure Regions are locations (e.g., eastus, westeurope).",
                    "Find the Location of your Virtual Network in the Virtual networks panel of the Azure Portal."
                ]
            },
            vpc: {
                title: "Azure VNet Name",
                steps: [
                    "Go to Virtual networks in the Azure Portal.",
                    "Copy your Virtual Network Name.",
                    "Note the Resource Group name containing the VNet (e.g., RG-DMS-Migration)."
                ]
            },
            asn: {
                title: "Azure BGP ASN",
                steps: [
                    "BGP Autonomous System Number for the Azure Virtual Network Gateway.",
                    "Azure Virtual Network Gateway default ASN is 65515.",
                    "For custom ASNs, you can use any private ASN in the 64512-65534 range."
                ]
            }
        }
    };

    const data = helpData[cloud]?.[field];
    if (!data) return null;

    return (
        <div>
            <h5 className="tooltip-title">{data.title}</h5>
            <ol className="tooltip-steps">
                {data.steps.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
        </div>
    );
};

const PrivateConnectivity = ({ connections, jobs, setNotify, onNavigateToSettings, onNavigateToMigrations }) => {
    const [sourceCloud, setSourceCloud] = useState(() => localStorage.getItem('vpn_sourceCloud') || 'aws');
    const [targetCloud, setTargetCloud] = useState(() => localStorage.getItem('vpn_targetCloud') || 'gcp');
    const [vpnConfig, setVpnConfig] = useState(() => {
        const saved = localStorage.getItem('vpn_config');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { }
        }
        return {
            bridge_name: 'dms-migration-bridge',
            aws_region: 'ap-south-1', aws_credential_id: '', aws_asn: 65001, aws_vpc_name: '',
            gcp_region: 'asia-south1', vpc_name: '', gcp_credential_id: '', gcp_asn: 65000,
            azure_region: 'eastus', azure_credential_id: '', azure_asn: 65002, azure_vnet_name: '', azure_resource_group: '',
            cidr_block: '10.0.0.0/16'
        };
    });
    const [availableCreds, setAvailableCreds] = useState({ aws: [], gcp: [], azure: [] });
    const [credentialModalProvider, setCredentialModalProvider] = useState(null);
    const [discoveredVpcs, setDiscoveredVpcs] = useState({ aws: [], gcp: [], azure: [] });
    const [loadingVpcs, setLoadingVpcs] = useState({ aws: false, gcp: false, azure: false });
    const [vpnLogs, setVpnLogs] = useState([]);
    const [vpnRunning, setVpnRunning] = useState(false);
    const [vpnCurrentCommand, setVpnCurrentCommand] = useState(null);
    const [vpnStatus, setVpnStatus] = useState(null);
    const [refreshingStatus, setRefreshingStatus] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [destroyWord, setDestroyWord] = useState('');
    const [isTerminalExpanded, setIsTerminalExpanded] = useState(false);
    const terminalRef = useRef(null);
    const modalTerminalRef = useRef(null);

    const [activeTooltip, setActiveTooltip] = useState({ role: null, field: null });

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (!e.target.closest('.field-help-btn') && !e.target.closest('.field-tooltip-popover')) {
                setActiveTooltip({ role: null, field: null });
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    const toggleTooltip = (role, field) => {
        setActiveTooltip(prev => {
            if (prev.role === role && prev.field === field) {
                return { role: null, field: null };
            }
            return { role, field };
        });
    };

    useEffect(() => {
        if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
        if (modalTerminalRef.current) {
            modalTerminalRef.current.scrollTop = modalTerminalRef.current.scrollHeight;
        }
    }, [vpnLogs, isTerminalExpanded]);

    const fetchVpnStatus = useCallback(async () => {
        setRefreshingStatus(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/status`);
            const data = await res.json();
            if (data.success) setVpnStatus(data);
        } catch (e) {
            console.error('Failed to fetch VPN status', e);
        } finally {
            setTimeout(() => setRefreshingStatus(false), 500);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/logs`);
            const data = await res.json();
            if (data.success && data.logs) setVpnLogs(data.logs);
        } catch (e) {
            console.error('Failed to fetch VPN logs', e);
        }
    }, []);

    const fetchCredentials = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/credentials`);
            const data = await res.json();
            if (data.success) setAvailableCreds(data);
        } catch (err) {
            console.error('Failed to fetch VPN credentials', err);
        }
    }, []);

    const handleCredentialSaved = useCallback((newCredentialId) => {
        const cloud = credentialModalProvider;
        setCredentialModalProvider(null);
        if (newCredentialId) {
            setVpnConfig(prev => ({ ...prev, [`${cloud}_credential_id`]: newCredentialId }));
        }
        fetchCredentials();
    }, [credentialModalProvider, fetchCredentials]);

    const discoverVpcs = useCallback(async (provider, credId, region) => {
        if (!credId || !region) return;
        setLoadingVpcs(prev => ({ ...prev, [provider]: true }));
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/discover/vpc/${provider}/${credId}/${region}`);
            const data = await res.json();
            if (data.success) {
                setDiscoveredVpcs(prev => ({ ...prev, [provider]: data.vpcs }));
            }
        } catch (e) {
            console.error(`Discovery failed for ${provider}`, e);
        } finally {
            setLoadingVpcs(prev => ({ ...prev, [provider]: false }));
        }
    }, []);

    useEffect(() => {
        fetchCredentials();
    }, [fetchCredentials]);

    useEffect(() => {
        fetchVpnStatus();
    }, [fetchVpnStatus]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        localStorage.setItem('vpn_sourceCloud', sourceCloud);
        localStorage.setItem('vpn_targetCloud', targetCloud);
        localStorage.setItem('vpn_config', JSON.stringify(vpnConfig));
    }, [sourceCloud, targetCloud, vpnConfig]);

    useEffect(() => {
        if (vpnConfig.aws_credential_id && vpnConfig.aws_region) discoverVpcs('aws', vpnConfig.aws_credential_id, vpnConfig.aws_region);
    }, [vpnConfig.aws_credential_id, vpnConfig.aws_region, discoverVpcs]);

    useEffect(() => {
        if (vpnConfig.gcp_credential_id && vpnConfig.gcp_region) discoverVpcs('gcp', vpnConfig.gcp_credential_id, vpnConfig.gcp_region);
    }, [vpnConfig.gcp_credential_id, vpnConfig.gcp_region, discoverVpcs]);

    useEffect(() => {
        if (vpnConfig.azure_credential_id && vpnConfig.azure_region) discoverVpcs('azure', vpnConfig.azure_credential_id, vpnConfig.azure_region);
    }, [vpnConfig.azure_credential_id, vpnConfig.azure_region, discoverVpcs]);

    const handleSaveVpnConfig = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/save-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...vpnConfig, source_cloud: sourceCloud, target_cloud: targetCloud })
            });
            const data = await res.json();
            if (data.success) {
                setNotify({ type: 'success', message: 'VPN Configuration saved.' });
            }
        } catch (e) {
            setNotify({ type: 'error', message: 'Error saving config.' });
        }
    };

    const swapClouds = () => {
        const oldS = sourceCloud;
        setSourceCloud(targetCloud);
        setTargetCloud(oldS);
    };

    const handleVpnCommand = (cmd) => {
        setVpnRunning(true);
        setVpnCurrentCommand(cmd);

        let actualCmd = cmd;
        if (cmd === 'init-plan') {
            actualCmd = 'init';
        }

        if (actualCmd === 'init') setVpnLogs([]);

        const startConnection = (runCmd) => {
            const evtSource = new EventSource(`${API_BASE_URL}/api/vpn/run/${runCmd}`);
            evtSource.onmessage = (e) => {
                if (e.data === 'DONE') {
                    evtSource.close();

                    if (cmd === 'init-plan' && runCmd === 'init') {
                        startConnection('plan');
                        return;
                    }

                    setVpnRunning(false);
                    setVpnCurrentCommand(null);
                    if (runCmd === 'apply' || runCmd === 'destroy') {
                        fetchVpnStatus();
                        if (runCmd === 'apply') {
                            setShowSuccessModal(true);
                        }
                    }
                    return;
                }
                setVpnLogs(prev => [...prev, e.data]);
            };
            evtSource.onerror = () => {
                evtSource.close();
                setVpnRunning(false);
                setVpnCurrentCommand(null);
            };
        };

        startConnection(actualCmd);
    };

    const renderLogLine = (log, index) => {
        let isError = log.startsWith('[ERROR]');
        let content = log.replace('[SYSTEM] ', '').replace('[ERROR] ', '');

        let rowClass = 'log-line';
        if (isError) rowClass += ' err';

        // Terraform highlighting
        if (content.trim().startsWith('+ ')) rowClass += ' add';
        else if (content.trim().startsWith('- ')) rowClass += ' destroy';
        else if (content.trim().startsWith('~ ')) rowClass += ' change';
        else if (content.includes('Process exited with code 0')) rowClass += ' success-exit';
        else if (content.includes('Plan:') && content.includes('to add')) rowClass += ' plan-summary';

        return (
            <div key={index} className={rowClass}>
                <span className={`log-prefix ${isError ? 'err' : 'sys'}`}>
                    {isError ? '[ERROR]' : '[SYSTEM]'}
                </span>
                <span className="log-content">{content}</span>
            </div>
        );
    };

    const renderCloudSettings = (role, cloud) => {
        const getVpcValue = () => {
            if (cloud === 'aws') return vpnConfig.aws_vpc_name;
            if (cloud === 'azure') return vpnConfig.azure_vnet_name;
            return vpnConfig.vpc_name;
        };

        const setVpcValue = (val) => {
            const field = cloud === 'aws' ? 'aws_vpc_name' : cloud === 'azure' ? 'azure_vnet_name' : 'vpc_name';
            setVpnConfig(prev => ({ ...prev, [field]: val }));
        };

        return (
            <div className={`provider-section ${cloud} ${role}`}>
                <div className="provider-label">
                    <ShieldCheck size={14} /> {role === 'source' ? 'CLOUD 1' : 'CLOUD 2'}: {cloud.toUpperCase()}
                </div>
                <div className="form-group">
                    <label className="flex-label">
                        Credentials
                        <button
                            type="button"
                            className="field-help-btn"
                            onClick={() => toggleTooltip(role, 'creds')}
                            title={`Where to find ${cloud.toUpperCase()} credentials`}
                        >
                            <HelpCircle size={13} />
                        </button>
                        {activeTooltip.role === role && activeTooltip.field === 'creds' && (
                            <div className="field-tooltip-popover">
                                <div className="tooltip-arrow" style={{ left: '76px' }}></div>
                                {getHelpContent(cloud, 'creds')}
                            </div>
                        )}
                    </label>
                    <select
                        className="form-select"
                        value={vpnConfig[`${cloud}_credential_id`] || ''}
                        onChange={e => {
                            if (e.target.value === 'create_new') {
                                setCredentialModalProvider(cloud);
                                return;
                            }
                            setVpnConfig({ ...vpnConfig, [`${cloud}_credential_id`]: e.target.value });
                        }}
                    >
                        <option value="">Select Credentials...</option>
                        {availableCreds[cloud]?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        <option value="create_new" style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>+ Create New Credentials</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="flex-label">
                        Region
                        <button
                            type="button"
                            className="field-help-btn"
                            onClick={() => toggleTooltip(role, 'region')}
                            title={`Where to find ${cloud.toUpperCase()} region`}
                        >
                            <HelpCircle size={13} />
                        </button>
                        {activeTooltip.role === role && activeTooltip.field === 'region' && (
                            <div className="field-tooltip-popover">
                                <div className="tooltip-arrow" style={{ left: '46px' }}></div>
                                {getHelpContent(cloud, 'region')}
                            </div>
                        )}
                    </label>
                    <input
                        type="text"
                        className="form-input"
                        value={vpnConfig[`${cloud}_region`] || ''}
                        onChange={e => setVpnConfig({ ...vpnConfig, [`${cloud}_region`]: e.target.value })}
                        placeholder={cloud === 'aws' ? 'ap-south-1' : cloud === 'azure' ? 'eastus' : 'asia-south1'}
                    />
                </div>
                <div className="form-group">
                    <label className="flex-label">
                        VPC Selection&nbsp;
                        {loadingVpcs[cloud] ? (
                            <RefreshCw size={10} className="spin loader-spaced" />
                        ) : discoveredVpcs[cloud]?.length > 0 ? (
                            <span className="discovery-pill success">{discoveredVpcs[cloud].length} FOUND</span>
                        ) : (
                            <span className="discovery-pill idle">Searching...</span>
                        )}
                        <button
                            type="button"
                            className="field-help-btn"
                            onClick={() => toggleTooltip(role, 'vpc')}
                            title={`Where to find ${cloud.toUpperCase()} VPC`}
                            style={{ marginLeft: 'auto' }}
                        >
                            <HelpCircle size={13} />
                        </button>
                        {activeTooltip.role === role && activeTooltip.field === 'vpc' && (
                            <div className="field-tooltip-popover right-aligned">
                                <div className="tooltip-arrow" style={{ right: '10px', left: 'auto' }}></div>
                                {getHelpContent(cloud, 'vpc')}
                            </div>
                        )}
                    </label>
                    <select
                        className="form-select"
                        value={getVpcValue()}
                        onChange={e => setVpcValue(e.target.value)}
                    >
                        <option value="">-- Choose {cloud.toUpperCase()} {cloud === 'aws' ? 'VPC' : cloud === 'azure' ? 'VNet' : 'Network'} --</option>
                        {discoveredVpcs[cloud]?.map(v => (
                            <option key={v.id} value={v.id}>{v.name} ({v.id})</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label className="flex-label">
                        BGP ASN
                        <button
                            type="button"
                            className="field-help-btn"
                            onClick={() => toggleTooltip(role, 'asn')}
                            title={`Where to find ${cloud.toUpperCase()} BGP ASN`}
                        >
                            <HelpCircle size={13} />
                        </button>
                        {activeTooltip.role === role && activeTooltip.field === 'asn' && (
                            <div className="field-tooltip-popover">
                                <div className="tooltip-arrow" style={{ left: '56px' }}></div>
                                {getHelpContent(cloud, 'asn')}
                            </div>
                        )}
                    </label>
                    <input
                        type="number"
                        className="form-input"
                        value={vpnConfig[`${cloud}_asn`] || 0}
                        onChange={e => setVpnConfig({ ...vpnConfig, [`${cloud}_asn`]: parseInt(e.target.value) || 0 })}
                    />
                </div>
            </div>
        );
    };

    const getTunnelContext = (tunnelId) => {
        const numId = parseInt(tunnelId);
        const job = jobs?.find(j => j.source_id === numId || j.target_id === numId);

        let sourceConn = null;
        let targetConn = null;

        if (job) {
            sourceConn = connections?.find(c => c.id === job.source_id);
            targetConn = connections?.find(c => c.id === job.target_id);
        } else {
            sourceConn = connections?.find(c => c.id === numId);
        }

        return {
            jobName: job?.name,
            sourceName: sourceConn?.name,
            targetName: targetConn?.name,
            connectionName: sourceConn?.name || `Conn #${tunnelId}`
        };
    };

    return (
        <div className="dms-page-container connectivity-container">
            <div className="dms-page-header section-header-modern">
                <div className="header-title-stack">
                    <h2>Private Connectivity</h2>
                    <p className="header-subtitle">Secure bridges for cross-cloud and on-premise migrations</p>
                </div>
                <div className="header-actions">
                    <button className="help-btn" onClick={() => setShowHelp(true)}>
                        <HelpCircle size={16} /> Setup Guide
                    </button>
                    {vpnStatus?.configured && (
                        <div className="status-badge-active">
                            <CheckCircle size={14} /> Bridge Active
                        </div>
                    )}
                </div>
            </div>

            <div className="connectivity-grid">
                <div className="main-config-layout">
                    <div className="config-slates-container">
                        <div className="config-card animate-in">
                            <div className="card-header">
                                <div className="icon-wrap vpn"><Network size={20} /></div>
                                <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                                    {sourceCloud.toUpperCase()}
                                    <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                                        <ArrowRightLeft size={16} />
                                    </span>
                                    {targetCloud.toUpperCase()} VPN Setup
                                </h3>
                            </div>

                            <div className="multi-cloud-selector">
                                <div className="selector-group">
                                    <label>CLOUD PROVIDER 1</label>
                                    <div className="cloud-chips">
                                        {['aws', 'gcp', 'azure'].map(c => (
                                            <button key={c} className={`cloud-chip ${sourceCloud === c ? 'active' : ''}`} onClick={() => setSourceCloud(c)}>
                                                <ProviderLogo provider={c} size={26} /> {c.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button className="swap-btn" onClick={swapClouds}><Shuffle size={18} /></button>
                                <div className="selector-group">
                                    <label>CLOUD PROVIDER 2</label>
                                    <div className="cloud-chips">
                                        {['aws', 'gcp', 'azure'].map(c => (
                                            <button key={c} className={`cloud-chip ${targetCloud === c ? 'active' : ''}`} onClick={() => setTargetCloud(c)}>
                                                <ProviderLogo provider={c} size={26} /> {c.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="provider-grid">
                                {renderCloudSettings('source', sourceCloud)}
                                {renderCloudSettings('target', targetCloud)}
                            </div>

                            <div className="shared-config-area">
                                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                    <label>Bridge Resource Name (Prefix)</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={vpnConfig.bridge_name}
                                        onChange={e => setVpnConfig({ ...vpnConfig, bridge_name: e.target.value })}
                                        placeholder="dms-migration-bridge"
                                    />
                                    <p className="input-hint"><Info size={10} /> Used to name Gateways, Routers, and Tunnels in the cloud console.</p>
                                </div>
                                <div className="form-group">
                                    <label>Shared Network CIDR Block</label>
                                    <div className="input-with-button">
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={vpnConfig.cidr_block}
                                            onChange={e => setVpnConfig({ ...vpnConfig, cidr_block: e.target.value })}
                                        />
                                        <button onClick={handleSaveVpnConfig} className="save-config-btn"><Save size={14} /> Save Config</button>
                                    </div>
                                    <p className="input-hint"><Info size={10} /> Subnets will be automatically carved from this block.</p>
                                </div>
                            </div>

                            <div className="terraform-terminal-wrap animate-in">
                                <div className="terminal-header">
                                    <div className="terminal-dots">
                                        <span className="dot red"></span>
                                        <span className="dot yellow"></span>
                                        <span className="dot green"></span>
                                    </div>
                                    <span className="terminal-title">Infrastructure Provisioning (Terraform)</span>
                                    <button
                                        className="terminal-expand-btn"
                                        onClick={() => setIsTerminalExpanded(true)}
                                        title="Expand View"
                                    >
                                        <Maximize2 size={16} />
                                    </button>
                                </div>
                                <div className="terminal-controls">
                                    <button onClick={() => handleVpnCommand('init-plan')} disabled={vpnRunning} className="step-btn">
                                        {vpnRunning && (vpnCurrentCommand === 'init' || vpnCurrentCommand === 'plan' || vpnCurrentCommand === 'init-plan') ? (
                                            <RefreshCw size={14} className="spin" />
                                        ) : (
                                            <Zap size={14} />
                                        )} Init & Plan
                                    </button>
                                    <button onClick={() => handleVpnCommand('apply')} disabled={vpnRunning} className="apply-btn">
                                        {vpnRunning && vpnCurrentCommand === 'apply' ? <RefreshCw size={14} className="spin" /> : 'Apply'}
                                    </button>
                                    <button className="destroy-btn-ref" onClick={() => setShowDestroyConfirm(true)} disabled={vpnRunning}>
                                        {vpnRunning && vpnCurrentCommand === 'destroy' ? <RefreshCw size={14} className="spin" /> : <><Trash2 size={14} /> Destroy</>}
                                    </button>
                                </div>
                                <div className="terminal-body" id="vpn-terminal-scroll" ref={terminalRef}>
                                    {vpnLogs.length === 0 ? (
                                        <div className="log-placeholder">Ready to provision infrastructure...</div>
                                    ) : (
                                        <>
                                            {vpnLogs.map((log, i) => renderLogLine(log, i))}
                                            {!vpnRunning && (
                                                <div className={`log-prompt ${vpnStatus?.configured ? '' : 'offline'}`}>
                                                    {vpnStatus?.configured ? (
                                                        vpnStatus.statusItems?.every(s => s.isUp) ?
                                                            '[SUCCESS] Infrastructure is ACTIVE. System Ready.' :
                                                            '[WARNING] Bridge established with partial connectivity issues.'
                                                    ) : (
                                                        '[ALERT] Bridge is OFFLINE. Infrastructure destroyed or not yet provisioned.'
                                                    )}
                                                    <span className="cursor"></span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="status-side-panel redesign-console">
                        <div className={`status-card-ref blue-gradient ${vpnStatus?.configured ? '' : 'offline'}`}>
                            <div className="status-header-min left" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Current VPN Status</span>
                                <RefreshCw size={14} className={`refresh-icon-min ${refreshingStatus ? 'spin' : ''}`} onClick={() => fetchVpnStatus()} />
                            </div>

                            {vpnStatus?.configured ? (
                                <>
                                    <div className="status-visualization">
                                        <div className="heartbeat-ring">
                                            <Activity size={32} className={vpnStatus.statusItems?.every(s => s.isUp) ? 'pulse-icon-v2' : 'pulse-icon-v2-warn'} />
                                        </div>
                                        <div className={`status-tag-v2 ${vpnStatus.statusItems?.every(s => s.isUp) ? 'success' : 'warning'}`}>
                                            {vpnStatus.statusItems?.every(s => s.isUp) ? 'Fully Operational' : 'Partial Connectivity'}
                                        </div>
                                    </div>
                                    <div className="status-metas-grid">
                                        {vpnStatus.statusItems?.map((item, idx) => (
                                            <div key={idx} className="meta-item">
                                                <div className="lbl-group">
                                                    <ProviderLogo provider={item.provider} size={24} />
                                                    <span className="lbl">{item.label}</span>
                                                </div>
                                                <span className={`val ${item.isUp ? 'success' : 'error'}`}>
                                                    {item.status}
                                                </span>
                                            </div>
                                        ))}
                                        {!vpnStatus.statusItems?.length && (
                                            <div className="meta-item">
                                                <span className="lbl">Infrastructure</span>
                                                <span className="val success">PROVISIONED</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="vpn-status-body-redesign">
                                    <div className="status-visualization-redesign">
                                        <div className="icon-circle-clear">
                                            <Network size={36} color="#64748b" />
                                        </div>
                                        <div className="status-lozenge-red">OFFLINE / DESTROYED</div>
                                    </div>

                                    <div className="status-metas-grid-redesign">
                                        <div className="meta-item-redesign">
                                            <div className="meta-icon-container">
                                                <ProviderLogo provider={sourceCloud} size={32} />
                                            </div>
                                            <div className="meta-info-redesign">
                                                <div className="meta-status-circle offline"></div>
                                                <span className="lbl">{sourceCloud.toUpperCase()} INTERFACE</span>
                                            </div>
                                            <span className="val-redesign error">DOWN</span>
                                        </div>
                                        <div className="meta-item-redesign">
                                            <div className="meta-icon-container">
                                                <ProviderLogo provider={targetCloud} size={32} />
                                            </div>
                                            <div className="meta-info-redesign">
                                                <div className="meta-status-circle offline"></div>
                                                <span className="lbl">{targetCloud.toUpperCase()} INTERFACE</span>
                                            </div>
                                            <span className="val-redesign error">DOWN</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="ssh-summary-card-ref lavender-accent">
                            <div className="status-header-min left">
                                <span>Active SSH Tunnels</span>
                            </div>
                            <div className="ssh-body-redesign">
                                {vpnStatus?.activeTunnels?.length > 0 ? (
                                    <div className="ssh-items-list-ref">
                                        {vpnStatus.activeTunnels.map(tunnel => {
                                            const ctx = getTunnelContext(tunnel.connectionId);
                                            return (
                                                <div key={tunnel.connectionId} className={`ssh-slate-item ${tunnel.active ? 'active' : 'idle'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px' }}>
                                                    <div className="name" style={{ fontWeight: 'bold' }}>{ctx.jobName || ctx.connectionName}</div>
                                                    <div style={{ fontWeight: 600, textTransform: 'uppercase', color: tunnel.active ? '#10b981' : '#ef4444', fontSize: '0.75rem' }}>
                                                        {tunnel.active ? 'Active' : 'Idle'}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="ssh-empty-state-redesign">
                                        <h4 className="ssh-empty-main">There are currently 0 active diagnostic tunnels.</h4>
                                        <p className="ssh-empty-sub">Standard monitoring is enabled; no tunnels require action.</p>
                                    </div>
                                )}
                            </div>
                        </div>


                    </div>
                </div>
            </div>

            {showHelp && (
                <div className="help-overlay-ref" onClick={() => setShowHelp(false)}>
                    <div className="help-modal-ref" onClick={e => e.stopPropagation()}>
                        <div className="help-modal-header-ref">
                            <div className="header-title-stack">
                                <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <HelpCircle size={24} style={{ color: 'var(--primary)' }} /> Setup Guide
                                </h3>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Follow these steps to establish a secure cross-cloud migration bridge using Terraform</p>
                            </div>
                            <button className="help-modal-close-btn" onClick={() => setShowHelp(false)} title="Close Setup Guide">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="help-modal-body-ref" style={{ marginTop: '0.5rem', maxHeight: '65vh', overflowY: 'auto', paddingRight: '12px' }}>

                            <div className="guide-prerequisites-card">
                                <h4>
                                    <AlertTriangle size={18} /> Pre-requisites
                                </h4>
                                <ul className="guide-prerequisites-list">
                                    <li><strong>Saved Credentials:</strong> Valid cloud credentials (AWS Access Keys & GCP Service Account JSON keys) must be configured in the <strong>Settings</strong> module.</li>
                                    <li><strong>IAM Roles & Permissions:</strong> Ensure your credentials have adequate rights to manage VPC subnets, route tables, security groups, and VPN gateways.</li>
                                    <li><strong>Shared Network CIDR Block:</strong> Allocate a unique CIDR (e.g., <code>10.0.0.0/16</code>) to carve out the VPN gateway subnets. Ensure this block does not overlap with any of your source or destination VPC ranges.</li>
                                </ul>
                            </div>

                            <div className="timeline-stepper">
                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 1</span>
                                    <h4 className="timeline-step-title">
                                        <Settings size={16} color="var(--primary)" /> Select Providers & Credentials
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Choose your Cloud Provider 1 and Cloud Provider 2 (e.g., AWS ⇄ GCP). Assign your stored credentials profile and region for each network. If you haven't created one, click <strong>+ Create New Credentials</strong>.
                                    </p>
                                </div>

                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 2</span>
                                    <h4 className="timeline-step-title">
                                        <Layers size={16} color="var(--secondary)" /> Region & VPC Discovery
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Enter your target cloud regions. The system will automatically perform dynamic API discovery using your credentials to fetch and populate available VPC networks in the dropdown. Select the matching VPC for each provider.
                                    </p>
                                </div>

                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 3</span>
                                    <h4 className="timeline-step-title">
                                        <Save size={16} color="var(--primary)" /> Save Variable Configuration
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Provide a <strong>Bridge Resource Name</strong> prefix (e.g., <code>dms-migration-bridge</code>) to identify your cloud infrastructure resources. Click the <strong>Save Config</strong> button to commit configurations and generate the <code>terraform.tfvars</code> parameters file on the server.
                                    </p>
                                </div>

                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 4</span>
                                    <h4 className="timeline-step-title">
                                        <Terminal size={16} color="var(--secondary)" /> Initialize & Provision Infrastructure
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Utilize the inline interactive console: Click <strong>Init</strong> to download cloud provider plugins, then click <strong>Apply</strong> to automatically compile and provision your IPsec VPN gateways, customer gateways, tunnels, and BGP routing. Check the live terminal output logs for feedback.
                                    </p>
                                </div>

                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 5</span>
                                    <h4 className="timeline-step-title">
                                        <CheckCircle size={16} color="var(--primary)" /> Verify Tunnel Status & Routing
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Once provisioned successfully, inspect the <strong>Current VPN Status</strong> panel to confirm it transitions to <strong>Fully Operational</strong>. Follow the propagation and security group instructions in the <strong>AWS Connectivity Checklist</strong> to allow traffic to reach your databases.
                                    </p>
                                </div>

                                <div className="timeline-item">
                                    <span className="timeline-step-pill">Step 6</span>
                                    <h4 className="timeline-step-title">
                                        <Compass size={16} color="var(--secondary)" /> Bind to Migration Wizard
                                    </h4>
                                    <p className="timeline-step-desc">
                                        Navigate to the <strong>Migrations</strong> page and create or edit a migration job. At Step 4 of the wizard (<strong>Connectivity Method</strong>), select the <strong>HA VPN Bridge</strong> routing option to tunnel snapshots and CDC transactions securely.
                                    </p>
                                </div>
                            </div>

                            <button onClick={() => setShowHelp(false)} className="cancel-v2" style={{ width: '100%', padding: '12px', borderRadius: '12px', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-main)', transition: 'all 0.2s' }}>Close Guide</button>
                        </div>
                    </div>
                </div>
            )}

            {showDestroyConfirm && (
                <div className="help-overlay-ref" onClick={() => { setShowDestroyConfirm(false); setDestroyWord(''); }}>
                    <div className="help-modal-ref destroy-warn-box" onClick={e => e.stopPropagation()}>
                        <div className="destroy-icon-wrapper">
                            <AlertTriangle size={48} color="#ef4444" />
                        </div>
                        <h3 className="destroy-title">Destroy Infrastructure?</h3>
                        <p className="destroy-desc">
                            This will <strong>permanently remove</strong> all migration gateways, VPN tunnels, and associated network resources. This action cannot be undone.
                        </p>

                        <div className="confirm-input-group">
                            <label>Please type <strong>DESTROY</strong> to confirm:</label>
                            <input
                                type="text"
                                className="confirm-input"
                                value={destroyWord}
                                onChange={(e) => setDestroyWord(e.target.value.toUpperCase())}
                                placeholder="Type DESTROY here"
                                autoFocus
                            />
                        </div>

                        <div className="modal-actions-ref">
                            <button className="cancel-v2" onClick={() => { setShowDestroyConfirm(false); setDestroyWord(''); }}>
                                Cancel
                            </button>
                            <button
                                className={`danger-v2 ${destroyWord === 'DESTROY' ? 'active' : 'disabled'}`}
                                onClick={() => {
                                    if (destroyWord === 'DESTROY') {
                                        handleVpnCommand('destroy');
                                        setShowDestroyConfirm(false);
                                        setDestroyWord('');
                                    }
                                }}
                                disabled={destroyWord !== 'DESTROY'}
                            >
                                {destroyWord === 'DESTROY' ? 'Yes, Destroy Everything' : 'Confirm Action First'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isTerminalExpanded && (
                <div className="help-overlay-ref" onClick={() => setIsTerminalExpanded(false)}>
                    <div className="terminal-modal-ref" onClick={e => e.stopPropagation()}>
                        <div className="terminal-modal-header">
                            <div className="terminal-dots">
                                <span className="dot red"></span>
                                <span className="dot yellow"></span>
                                <span className="dot green"></span>
                            </div>
                            <span className="terminal-title">Infrastructure Provisioning (Terraform)</span>
                            <button className="terminal-close-btn" onClick={() => setIsTerminalExpanded(false)} title="Close Expanded View">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="terminal-controls" style={{ marginBottom: '1.5rem' }}>
                            <button onClick={() => handleVpnCommand('init-plan')} disabled={vpnRunning} className="step-btn">
                                {vpnRunning && (vpnCurrentCommand === 'init' || vpnCurrentCommand === 'plan' || vpnCurrentCommand === 'init-plan') ? (
                                    <RefreshCw size={14} className="spin" />
                                ) : (
                                    <Zap size={14} />
                                )} Init & Plan
                            </button>
                            <button onClick={() => handleVpnCommand('apply')} disabled={vpnRunning} className="apply-btn">
                                {vpnRunning && vpnCurrentCommand === 'apply' ? <RefreshCw size={14} className="spin" /> : 'Apply'}
                            </button>
                            <button className="destroy-btn-ref" onClick={() => { setIsTerminalExpanded(false); setShowDestroyConfirm(true); }} disabled={vpnRunning}>
                                {vpnRunning && vpnCurrentCommand === 'destroy' ? <RefreshCw size={14} className="spin" /> : <><Trash2 size={14} /> Destroy</>}
                            </button>
                        </div>
                        <div className="terminal-body" id="vpn-terminal-scroll-modal" ref={modalTerminalRef}>
                            {vpnLogs.length === 0 ? (
                                <div className="log-placeholder">Ready to provision infrastructure...</div>
                            ) : (
                                <>
                                    {vpnLogs.map((log, i) => renderLogLine(log, i))}
                                    {!vpnRunning && (
                                        <div className={`log-prompt ${vpnStatus?.configured ? '' : 'offline'}`}>
                                            {vpnStatus?.configured ? (
                                                vpnStatus.statusItems?.every(s => s.isUp) ?
                                                    '[SUCCESS] Infrastructure is ACTIVE. System Ready.' :
                                                    '[WARNING] Bridge established with partial connectivity issues.'
                                            ) : (
                                                '[ALERT] Bridge is OFFLINE. Infrastructure destroyed or not yet provisioned.'
                                            )}
                                            <span className="cursor"></span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div className="help-overlay-ref" onClick={() => setShowSuccessModal(false)}>
                    <div className="help-modal-ref success-modal-box" onClick={e => e.stopPropagation()}>
                        <button className="help-modal-close-btn" onClick={() => setShowSuccessModal(false)} title="Close Popup" style={{ alignSelf: 'flex-end', margin: '-10px -10px 10px 0' }}>
                            <X size={20} />
                        </button>

                        <div className="success-icon-wrapper">
                            <div className="success-heartbeat-ring">
                                <CheckCircle size={48} color="#10b981" />
                            </div>
                        </div>

                        <h3 className="success-title">VPN Connection Successful!</h3>
                        <p className="success-desc" style={{ marginBottom: '1.5rem', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                            The infrastructure has been provisioned successfully and the secure cross-cloud migration bridge is active.
                        </p>

                        <div className="modal-checklist-card" style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                            <div className="status-header-min" style={{ borderBottom: '1px solid var(--card-border)', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                                <span style={{ fontWeight: 'bold' }}>AWS Connectivity Checklist</span>
                                <ShieldCheck size={14} color="var(--portal-accent)" />
                            </div>
                            <div className="checklist-items" style={{ padding: '0.5rem 0' }}>
                                <div className="checklist-item" style={{ display: 'flex', gap: '10px', marginBottom: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    <div style={{ marginTop: '3px' }}><div className="status-dot-min active"></div></div>
                                    <div>
                                        <strong>Route Propagation</strong>
                                        <p style={{ margin: '2px 0 0', opacity: 0.7, fontSize: '0.8rem' }}>Enable for your RDS subnet's route table in AWS Console.</p>
                                    </div>
                                </div>
                                <div className="checklist-item" style={{ display: 'flex', gap: '10px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    <div style={{ marginTop: '3px' }}><div className="status-dot-min active"></div></div>
                                    <div>
                                        <strong>RDS Security Group</strong>
                                        <p style={{ margin: '2px 0 0', opacity: 0.7, fontSize: '0.8rem' }}>Attach <code>{vpnConfig.bridge_name || 'dms-migration-bridge'}-allow-gcp</code> to your database.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions-ref" style={{ marginTop: '1.5rem', display: 'flex', gap: '12px' }}>
                            <button className="cancel-v2" onClick={() => setShowSuccessModal(false)} style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-card)',
                                color: 'var(--text-main)'
                            }}>
                                Dismiss
                            </button>
                            <button className="success-btn-v2" onClick={() => {
                                setShowSuccessModal(false);
                                if (onNavigateToMigrations) {
                                    onNavigateToMigrations();
                                } else {
                                    window.location.hash = '#/dms/jobs';
                                }
                            }} style={{
                                flex: 1,
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '12px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}>
                                Go to Migrations
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {credentialModalProvider && (
                <ContextCredentialModal 
                    provider={credentialModalProvider} 
                    onClose={() => setCredentialModalProvider(null)} 
                    onSaved={handleCredentialSaved} 
                />
            )}
        </div>
    );
};

export default PrivateConnectivity;
